import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { PickupForfeitureModalComponent } from './viewbid/shared/components/pickup-forfeiture-modal/pickup-forfeiture-modal.component';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { httpTranslateLoader } from './http-translate-loader';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApplicationConfiguration } from './viewbid/shared/services/app.config';
import {
    AuthClientConfig,
    // AuthHttpInterceptor,
    AuthConfig,
    AuthModule as Auth0Module,
} from '@auth0/auth0-angular';
import {
    AuthEffects,
    AuthGuard,
    AuthModule as VBAuthModule,
    ViewBidSharedModule,
} from 'viewbid-shared-lib';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { SharedModule as VBSharedModule } from './viewbid/shared/shared.module';
import {
    ApiModule,
    Configuration,
    RegistrationService,
    ProfilesService,
    AuctionCatalogsService,
    BiddingService,
    NotificationsService,
    UsersService,
} from '@viewbid/ts-angular-client';
import { CreateProfileEffects } from './viewbid/modules/profiles/state/create-profile-effects';
import { provideBootstrapEffects } from './viewbid/utils/ngrx-utils';
import { SelectedLotEffects } from './viewbid/modules/auctions/state/selected-lot-state/selected-lot.effects';
import { SelectedAuctionEffects } from './viewbid/modules/auctions/state/selected-auction-state/selected-auction.effects';
import { AuctionsPageEffects } from './viewbid/modules/auctions/state/auctions-page-state/auctions-page.effects';
import { TitleCasePipe } from '@angular/common';
import { TokenInterceptor } from './token.interceptor';
import { WatchlistPageStateEffects } from './viewbid/modules/profiles/state/watchlist-page-state/watchlist-page-state.effects';
import { ModalModule } from 'ngx-bootstrap/modal';
import { UserBidHistoryStateEffects } from './viewbid/modules/profiles/state/bidding/user-bid-history.effects';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { environment } from '../environments/environment';
// import * as Sentry from '@sentry/angular';
import { GlobalErrorHandler } from './global-error-handler';
import { VbSnackbarHostComponent, VbSpinnerComponent } from '@viewbid/ui';

function configInitializer(
    applicationConfiguration: ApplicationConfiguration,
    auth0config: AuthClientConfig,
) {
    return async (): Promise<void> => {
        const res: any = await fetch('/api/users/config/bidder');
        const json: any = await res.json();
        const config: AuthConfig = {
            ...json['auth0'],
            skipRedirectCallback: false,
            authorizationParams: {
                ...json['auth0'].authorizationParams,
                redirect_uri: `${window.location.origin}/post-login`,
                // Only attempt authentication if not masquerading
                prompt: window.name === 'masquerade' ? 'none' : 'login',
            },
        };
        auth0config.set(config);
        applicationConfiguration.configuration = json;
    };
}

@NgModule({
    declarations: [AppComponent, PickupForfeitureModalComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        // vb-snackbar (@viewbid/ui) replaces ngx-toastr's ToastrModule — no
        // forRoot() config here, it uses its own VB_SNACKBAR_DEFAULTS.
        // Standalone; imported directly so AppComponent's template can use
        // its <vb-snackbar-host> selector.
        VbSnackbarHostComponent,
        // Standalone; imported directly (SharedModule imports it but
        // doesn't re-export it) so AppComponent's template can use
        // <vb-spinner> while Auth0 initializes.
        VbSpinnerComponent,
        Auth0Module.forRoot(),
        StoreModule.forRoot(),
        StoreDevtoolsModule.instrument(),
        EffectsModule.forRoot(),
        VBAuthModule,
        VBSharedModule,
        TitleCasePipe,
        RecaptchaV3Module,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoader,
                deps: [HttpClient],
            },
            isolate: false,
        }),
        ApiModule.forRoot((): Configuration => {
            const config: Configuration = new Configuration();
            return config;
        }),
        ModalModule.forRoot(),
        ViewBidSharedModule,
    ],
    providers: [
        // {
        //     provide: ErrorHandler,
        //     useValue: Sentry.createErrorHandler(),
        // },
        // {
        //     provide: Sentry.TraceService,
        //     deps: [Router],
        // },
        {
            provide: APP_INITIALIZER,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            useFactory: (): any => (): any => {},
            // deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: configInitializer,
            deps: [ApplicationConfiguration, AuthClientConfig],
            multi: true,
        },
        AuthGuard,
        ApplicationConfiguration,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        // Commented out Auth0's HTTP interceptor since we're using our JWT token interceptor instead
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: AuthHttpInterceptor,
        //     multi: true,
        // },
        {
            provide: RegistrationService,
            useFactory: (httpClient: HttpClient): RegistrationService =>
                new RegistrationService(
                    httpClient,
                    '/api/users',
                    new Configuration(),
                ),
            deps: [HttpClient],
        },
        {
            provide: ProfilesService,
            useFactory: (httpClient: HttpClient): ProfilesService =>
                new ProfilesService(
                    httpClient,
                    '/api/users',
                    new Configuration(),
                ),
            deps: [HttpClient],
        },
        {
            provide: AuctionCatalogsService,
            useFactory: (httpClient: HttpClient): AuctionCatalogsService =>
                new AuctionCatalogsService(
                    httpClient,
                    '/api/auction-catalogs',
                    new Configuration(),
                ),
            deps: [HttpClient],
        },
        {
            provide: BiddingService,
            useFactory: (httpClient: HttpClient): BiddingService =>
                new BiddingService(
                    httpClient,
                    '/api/bidding',
                    new Configuration(),
                ),
            deps: [HttpClient],
        },
        {
            provide: NotificationsService,
            useFactory: (httpClient: HttpClient): NotificationsService =>
                new NotificationsService(
                    httpClient,
                    '/api/notifications',
                    new Configuration(),
                ),
            deps: [HttpClient],
        },
        {
            provide: UsersService,
            useFactory: (httpClient: HttpClient): UsersService =>
                new UsersService(httpClient, '/api/users', new Configuration()),
            deps: [HttpClient],
        },
        provideBootstrapEffects([
            AuthEffects,
            CreateProfileEffects,
            WatchlistPageStateEffects,
            SelectedLotEffects,
            SelectedAuctionEffects,
            AuctionsPageEffects,
            UserBidHistoryStateEffects,
        ]),
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.recaptchaSiteKey,
        },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}
